import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    CreateBroadcast as CreateBroadcastMutation,
    CreateBroadcastVariables as CreateBroadcastMutationVariables,
    DuplicateBroadcast as DuplicateBroadcastMutation,
    DuplicateBroadcastVariables as DuplicateBroadcastMutationVariables,
    DeleteBroadcast as DeleteBroadcastMutation,
    DeleteBroadcastVariables as DeleteBroadcastMutationVariables,
    RestoreBroadcast as RestoreBroadcastMutation,
    RestoreBroadcastVariables as RestoreBroadcastMutationVariables,
    UpdateBroadcast as UpdateBroadcastMutation,
    UpdateBroadcastVariables as UpdateBroadcastMutationVariables,
    GetBroadcasts as GetBroadcastsQuery,
    GetBroadcast as GetBroadcastQuery,
    EditionUpdateInput,
    GetBroadcastsVariables as GetBroadcastsQueryVariables,
    FieldsToCompare,
} from 'nutshell-graphql-types';

import CREATE_BROADCAST from './graphql/mutations/create-broadcast.graphql';
import DELETE_BROADCAST from './graphql/mutations/delete-broadcast.graphql';
import DUPLICATE_BROADCAST from './graphql/mutations/duplicate-broadcast.graphql';
import RESTORE_BROADCAST from './graphql/mutations/restore-broadcast.graphql';
import UPDATE_BROADCAST from './graphql/mutations/update-broadcast.graphql';
import GET_BROADCAST from './graphql/queries/get-broadcast.graphql';
import GET_BROADCASTS from './graphql/queries/get-broadcasts.graphql';

type Props = {
    children: (arg1: {
        onCreate: (
            name: string,
            isABTested?: boolean,
            fieldsToCompare?: FieldsToCompare,
            queryVariables?: GetBroadcastsQueryVariables
        ) => Promise<any>;
        onDuplicate: (
            broadcastId: string,
            name?: string | null | undefined,
            queryVariables?: GetBroadcastsQueryVariables
        ) => Promise<any>;
        onDelete: (
            broadcastId: string,
            queryVariables?: GetBroadcastsQueryVariables
        ) => Promise<any>;
        onRestore: (
            broadcastId: string,
            queryVariables?: GetBroadcastsQueryVariables
        ) => Promise<any>;
        onUpdate: (input: EditionUpdateInput, broadcastId: string) => Promise<any>;
    }) => React.ReactElement;
};

export function BroadcastMutations(props: Props) {
    const [createBroadcast] = useMutation<
        CreateBroadcastMutation,
        CreateBroadcastMutationVariables
    >(CREATE_BROADCAST);

    const [duplicateBroadcast] = useMutation<
        DuplicateBroadcastMutation,
        DuplicateBroadcastMutationVariables
    >(DUPLICATE_BROADCAST);

    const [deleteBroadcast] = useMutation<
        DeleteBroadcastMutation,
        DeleteBroadcastMutationVariables
    >(DELETE_BROADCAST);

    const [restoreBroadcast] = useMutation<
        RestoreBroadcastMutation,
        RestoreBroadcastMutationVariables
    >(RESTORE_BROADCAST);

    const [updateBroadcast] = useMutation<
        UpdateBroadcastMutation,
        UpdateBroadcastMutationVariables
    >(UPDATE_BROADCAST);

    return props.children({
        onCreate: (
            name: string,
            isABTested?: boolean,
            fieldsToCompare?: FieldsToCompare,
            queryVariables?: GetBroadcastsQueryVariables
        ) => {
            return createBroadcast({
                variables: {
                    broadcast: {
                        name: name,
                        isABTested: isABTested,
                        fieldsToCompare: fieldsToCompare,
                    },
                },
                update: (proxy, mutationResults) => {
                    const newBroadcast =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.broadcastCreate &&
                        mutationResults.data.broadcastCreate.broadcast;

                    if (!newBroadcast) return;

                    broadcastCacheUpdate(proxy, newBroadcast, false, queryVariables);
                },
            }).then(
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.broadcastCreate &&
                    mutationResults.data.broadcastCreate.broadcast
            );
        },
        onDuplicate: (
            broadcastId: string,
            name?: string | null,
            queryVariables?: GetBroadcastsQueryVariables
        ) => {
            return duplicateBroadcast({
                variables: {broadcast: {editionId: broadcastId, name: name}},
                update: (proxy, mutationResults) => {
                    const newBroadcast =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionDuplicateToBroadcast &&
                        mutationResults.data.editionDuplicateToBroadcast.broadcast;

                    if (!newBroadcast) return;

                    broadcastCacheUpdate(proxy, newBroadcast, false, queryVariables);
                },
            }).then(
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.editionDuplicateToBroadcast &&
                    mutationResults.data.editionDuplicateToBroadcast.broadcast
            );
        },
        onDelete: (broadcastId: string, queryVariables?: GetBroadcastsQueryVariables) => {
            return deleteBroadcast({
                variables: {id: broadcastId},
                update: (proxy, mutationResults) => {
                    const deletedBroadcast =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignDelete &&
                        mutationResults.data.campaignDelete.campaign;

                    if (!deletedBroadcast) return;

                    broadcastCacheUpdate(proxy, deletedBroadcast, true, queryVariables);
                },
            });
        },
        onRestore: (broadcastId: string, queryVariables?: GetBroadcastsQueryVariables) => {
            return restoreBroadcast({
                variables: {id: broadcastId},
                update: (proxy, mutationResults) => {
                    const restoredBroadcast =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignRestore &&
                        mutationResults.data.campaignRestore.campaign;

                    if (!restoredBroadcast) return;

                    broadcastCacheUpdate(proxy, restoredBroadcast, false, queryVariables);
                },
            });
        },
        onUpdate: (saveData: EditionUpdateInput, broadcastId: string) => {
            return updateBroadcast({
                variables: {
                    edition: saveData,
                },
                update: (proxy, mutationResults) => {
                    // Read the data from our cache for this query.
                    const cacheResult: GetBroadcastQuery | null | undefined = proxy.readQuery({
                        query: GET_BROADCAST,
                        variables: {broadcastId: broadcastId},
                    });

                    if (!cacheResult || !cacheResult.broadcast) {
                        return;
                    }

                    const updatedEdition =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.editionUpdate &&
                        mutationResults.data.editionUpdate.edition;
                    if (!updatedEdition) return;

                    proxy.writeQuery({
                        query: GET_BROADCAST,
                        variables: {broadcastId: broadcastId},
                        data: {
                            ...cacheResult.broadcast,
                            edition: updatedEdition,
                            name: updatedEdition.campaign.name,
                        },
                    });
                },
            });
        },
    });
}

/**
 * This will handle updating our local cache when we archive or restore a
 * broadcast. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {Broadcast} broadcast The broadcast that was mutated
 * @param {boolean} isDeleting True if we are deleting the given broadcast,
 * false if we are restoring it.
 */
const broadcastCacheUpdate = (
    // @ts-expect-error
    proxy,
    // @ts-expect-error
    broadcast,
    isDeleting: boolean,
    queryVariables?: GetBroadcastsQueryVariables
) => {
    // Passing a value for `isArchived` here isn't really needed because we
    // overwrite it when writing to the cache below, but hey I made it required
    const variables = queryVariables || {};

    try {
        // Update our archived cache
        // This isn't the best variable name, but its the cache we're removing
        // an broadcast from.
        const cacheResultRemoving: GetBroadcastsQuery | null | undefined = proxy.readQuery({
            query: GET_BROADCASTS,
            variables: {...variables, archived: !isDeleting},
        });

        if (cacheResultRemoving && cacheResultRemoving.broadcasts) {
            proxy.writeQuery({
                query: GET_BROADCASTS,
                variables: {...variables, archived: !isDeleting},
                data: {
                    broadcasts: {
                        ...cacheResultRemoving.broadcasts,
                        edges: cacheResultRemoving.broadcasts.edges.filter((edge) => {
                            return edge.node.id !== broadcast.id;
                        }),
                    },
                },
            });
        }
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }

    // Update our unarchived cache
    // Again, not the best name. We're adding an broadcast to this cache
    try {
        const cacheResultAdding: GetBroadcastsQuery | null | undefined = proxy.readQuery({
            query: GET_BROADCASTS,
            variables: {...variables, archived: isDeleting},
        });

        if (!cacheResultAdding || !cacheResultAdding.broadcasts) {
            return;
        }

        proxy.writeQuery({
            query: GET_BROADCASTS,
            variables: {...variables, archived: isDeleting},
            data: {
                broadcasts: {
                    ...cacheResultAdding.broadcasts,
                    edges: [
                        {
                            cursor: broadcast.id,
                            node: broadcast,
                            __typename: 'AudiencesEdge',
                        },
                    ].concat(cacheResultAdding.broadcasts.edges),
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
